import GridViewIcon from "@mui/icons-material/GridView"
import ViewColumnIcon from "@mui/icons-material/ViewColumn"
import ViewStreamIcon from "@mui/icons-material/ViewStream"
import Box from "@mui/material/Box"
import Checkbox from "@mui/material/Checkbox"
import FormControlLabel from "@mui/material/FormControlLabel"
import Typography from "@mui/material/Typography"
import { t } from "i18next"
import type { AutoFrameI, FlexFrameI, FrameI } from "tweenly"
import ExpandableSection from "../../../common/ExpandableSection"
import CompactSelect from "../../../common/PropertyTools/CompactSelect"
import PrefixedInput from "../../../common/PropertyTools/PrefixedInput"
import PropertyRow from "../../../common/PropertyTools/PropertyRow"
import SegmentedButtonGroup from "../../../common/PropertyTools/SegmentedButtonGroup"

/** Properties of FlexFrameI that can be edited in FlexLayout panel */
type FlexLayoutProperty = "layout" | "gap" | "padding" | "justifyContent" | "alignItems" | "clip"

interface Props {
    object: FrameI | FlexFrameI | AutoFrameI
    handleSetObjectValue: <K extends FlexLayoutProperty>(property: K, value: FlexFrameI[K]) => void
    expanded?: boolean
}

const FlexLayout = ({ object, handleSetObjectValue, expanded = true }: Props) => {
    // Only render for flex-frame objects
    if (object.type !== "flex-frame") {
        return null
    }

    const flexObject = object as FlexFrameI

    const layout = flexObject.layout || "horizontal"
    const handleLayoutChange = (newLayout: FlexFrameI["layout"]) => {
        if (newLayout !== layout) handleSetObjectValue("layout", newLayout)
    }

    const handleNumberChange =
        (property: "gap" | "padding") => (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = Number(e.target.value)
            if (String(e.target.value).trim() === "" || !Number.isFinite(value)) return
            if (property === "padding") {
                // Convert single number to tuple for padding
                handleSetObjectValue("padding", [value, value, value, value])
            } else {
                handleSetObjectValue(property, value)
            }
        }

    const handleSelectChange =
        (property: "justifyContent" | "alignItems") => (e: React.ChangeEvent<HTMLInputElement>) => {
            handleSetObjectValue(property, e.target.value as FlexFrameI[typeof property])
        }

    const isFlex = flexObject.layout && flexObject.layout !== "none"
    const padding =
        typeof flexObject.padding === "number"
            ? flexObject.padding
            : Array.isArray(flexObject.padding)
            ? flexObject.padding[0]
            : 0

    return (
        <ExpandableSection
            title={t("editor:flexLayout")}
            expanded={expanded}
            component={
                // two to a row, like every other section
                <Box display="flex" flexDirection="column" gap={1.5} width="100%" pb={0.5}>
                    <PropertyRow>
                        <Box display="flex" gap={1}>
                            <Box flex={1} minWidth={0} display="flex">
                                <SegmentedButtonGroup
                                    segments={[
                                        {
                                            title: `${t("editor:layoutDirection")}: horizontal`,
                                            icon: <ViewStreamIcon fontSize="small" />,
                                            onClick: () => handleLayoutChange("horizontal"),
                                            selected: layout === "horizontal",
                                        },
                                        {
                                            title: `${t("editor:layoutDirection")}: vertical`,
                                            icon: <ViewColumnIcon fontSize="small" />,
                                            onClick: () => handleLayoutChange("vertical"),
                                            selected: layout === "vertical",
                                        },
                                        {
                                            title: `${t("editor:layoutDirection")}: none`,
                                            icon: <GridViewIcon fontSize="small" />,
                                            onClick: () => handleLayoutChange("none"),
                                            selected: layout === "none",
                                        },
                                    ]}
                                />
                            </Box>
                            <Box flex={1} />
                        </Box>
                    </PropertyRow>
                    {isFlex && (
                        <PropertyRow>
                            <Box display="flex" gap={1}>
                                <Box flex={1} minWidth={0}>
                                    <PrefixedInput
                                        name="gap"
                                        value={flexObject.gap || 0}
                                        label={t("editor:gap")}
                                        tooltip={t("editor:gap")}
                                        handleOnChange={handleNumberChange("gap")}
                                        min={0}
                                        max={200}
                                    />
                                </Box>
                                <Box flex={1} minWidth={0}>
                                    <PrefixedInput
                                        name="padding"
                                        value={padding}
                                        label={t("editor:padding")}
                                        tooltip={t("editor:padding")}
                                        handleOnChange={handleNumberChange("padding")}
                                        min={0}
                                        max={200}
                                    />
                                </Box>
                            </Box>
                        </PropertyRow>
                    )}
                    {isFlex && (
                        <PropertyRow>
                            <Box display="flex" gap={1}>
                                <Box flex={1} minWidth={0}>
                                    <CompactSelect
                                        name="justifyContent"
                                        value={flexObject.justifyContent || "start"}
                                        options={[
                                            { value: "start", label: t("editor:flexStart") },
                                            { value: "center", label: t("editor:flexCenter") },
                                            { value: "end", label: t("editor:flexEnd") },
                                            {
                                                value: "space-between",
                                                label: t("editor:flexSpaceBetween"),
                                            },
                                            {
                                                value: "space-around",
                                                label: t("editor:flexSpaceAround"),
                                            },
                                        ]}
                                        label={t("editor:justifyContent")}
                                        tooltip={t("editor:justifyContent")}
                                        handleOnChange={handleSelectChange("justifyContent")}
                                    />
                                </Box>
                                <Box flex={1} minWidth={0}>
                                    <CompactSelect
                                        name="alignItems"
                                        value={flexObject.alignItems || "stretch"}
                                        options={[
                                            { value: "stretch", label: t("editor:flexStretch") },
                                            { value: "start", label: t("editor:flexStart") },
                                            { value: "center", label: t("editor:flexCenter") },
                                            { value: "end", label: t("editor:flexEnd") },
                                        ]}
                                        label={t("editor:alignItems")}
                                        tooltip={t("editor:alignItems")}
                                        handleOnChange={handleSelectChange("alignItems")}
                                    />
                                </Box>
                            </Box>
                        </PropertyRow>
                    )}
                    <PropertyRow>
                        <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                                <Checkbox
                                    checked={flexObject.clip || false}
                                    onChange={(e) => handleSetObjectValue("clip", e.target.checked)}
                                    size="small"
                                />
                            }
                            label={
                                <Typography variant="body2">{t("editor:clipContent")}</Typography>
                            }
                        />
                    </PropertyRow>
                </Box>
            }
        />
    )
}

export default FlexLayout
